import { useEffect, useMemo, useState } from "react";
import { MultiLineChart } from "../ChartComponents";
import DashboardMetricsService from "../../services/dashboardMetricsService";
import { useDashboardUI } from "../../context/GlobalContext";
import { buildAnalyticsQueryParams } from "../../helpers/helpers";
// import { useDashboardUI } from "../context/GlobalContext";

/**
 * Merge multiple analytics arrays by date
 */
function mergeAnalyticsByDate(metrics) {
  const map = {};

  metrics.forEach(({ key, source, valueKey }) => {
    source.forEach(item => {
      if (!map[item.date]) {
        map[item.date] = { date: item.date };
      }
      map[item.date][key] = item[valueKey] || 0;
    });
  });

  return Object.values(map).sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );
}

/**
 * Metric configuration (SOURCE-AWARE)
 */
const METRIC_CONFIG = {
  projects_created: {
    source: "project",
    metricKey: "projects_created",
    chartKey: "projects",
    valueKey: "total_projects",
    label: "Projects",
    color: "#6366F1",
  },
  collections_created: {
    source: "collection",
    metricKey: "collections_created",
    chartKey: "collections",
    valueKey: "total_projects",
    label: "Collections",
    color: "#22C55E",
  },
  project_files_uploaded: {
    source: "project",
    metricKey: "files_uploaded",
    chartKey: "project_files",
    valueKey: "total_files",
    label: "Project Files",
    color: "#6366F1",
  },
  collection_files_uploaded: {
    source: "collection",
    metricKey: "files_uploaded",
    chartKey: "collection_files",
    valueKey: "total_files",
    label: "Collection Files",
    color: "#22C55E",
  },
  project_chats_created: {
    source: "project",
    metricKey: "chats_created",
    chartKey: "project_chats",
    valueKey: "total_chats",
    label: "Project Chats",
    color: "#6366F1",
  },
  collection_chats_created: {
    source: "collection",
    metricKey: "chats_created",
    chartKey: "collection_chats",
    valueKey: "total_chats",
    label: "Collection Chats",
    color: "#22C55E",
  },
};

/**
 * Reusable chart card
 */
function AnalyticsChartCard({ title, subtitle, chartData, lines, className = "" }) {
  const isEmpty =
    !chartData.length ||
    chartData.every(item =>
      lines.every(line => (item[line.dataKey] || 0) === 0)
    );

  /**
   * Calculate totals per line
   */
  const totals = lines.map(line => {
    const total = chartData.reduce(
      (sum, item) => sum + (item[line.dataKey] || 0),
      0
    );

    return {
      label: line.name,
      value: total,
      color: line.stroke,
    };
  });

  return (
    <div className={`min-w-0 w-full rounded-2xl border border-[#ECEEF4] bg-white p-5 sm:p-6 ${className}`}>
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-base font-bold tracking-tight text-[#14122B]">
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm text-slate-500">
            {subtitle}
          </p>
        )}
      </div>

      {/* Chart / Empty */}
      {isEmpty ? (
        <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
          No activity in this time range
        </div>
      ) : (
        <>
          <MultiLineChart
            data={chartData}
            xKey="date"
            lines={lines}
          />

          {/* Totals */}
          <div className="mt-4 border-t border-[#ECEEF4] pt-4">
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {totals.map(total => (
                <div
                  key={total.label}
                  className="flex items-center gap-2 text-sm"
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: total.color }}
                  />
                  <span className="text-slate-600">
                    Total {total.label}:
                  </span>
                  <span className="font-semibold text-slate-900">
                    {total.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


export default function ProjectAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const {
    selectedFacilityFilter,
    selectedStartDateFilter,
    selectedEndDateFilter,
  } = useDashboardUI();


  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(false);

      const queryParams = buildAnalyticsQueryParams({
        startValue: selectedStartDateFilter,
        endValue: selectedEndDateFilter,
        facilityId: selectedFacilityFilter?.facility_number,
      });

      const results = await Promise.allSettled([
        DashboardMetricsService.getProjectAnalytics(queryParams),
        DashboardMetricsService.getCollectionAnalytics(queryParams),
      ]);

      const [projectResult, collectionResult] = results;

      setAnalytics({
        project:
          projectResult.status === "fulfilled"
            ? projectResult.value.data
            : null,

        collection:
          collectionResult.status === "fulfilled"
            ? collectionResult.value.data
            : null,
      });

      // Optional: log failures without breaking UI
      if (projectResult.status === "rejected") {
        console.error("Project analytics failed", projectResult.reason);
      }

      if (collectionResult.status === "rejected") {
        console.error("Collection analytics failed", collectionResult.reason);
      }
    } catch (err) {
      // This should almost never trigger now
      console.error("Unexpected analytics error", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };


  /**
   * Fetch analytics (NAMESPACED)
   */
  useEffect(() => {
    fetchAnalytics();
  }, [selectedStartDateFilter, selectedEndDateFilter, selectedFacilityFilter])

  /**
   * Build chart data by metric keys
   */
  const buildChartData = metricKeys => {
    const activeMetrics = metricKeys.map(key => {
      const config = METRIC_CONFIG[key];
      const sourceData =
        analytics?.[config.source]?.[config.metricKey] || [];

      return {
        mergeConfig: {
          key: config.chartKey,
          source: sourceData,
          valueKey: config.valueKey,
        },
        lineConfig: {
          dataKey: config.chartKey,
          stroke: config.color,
          name: config.label,
        },
      };
    });

    return {
      chartData: mergeAnalyticsByDate(
        activeMetrics.map(m => m.mergeConfig)
      ),
      lines: activeMetrics.map(m => m.lineConfig),
    };
  };

  /**
   * Chart datasets
   */
  const projectChart = useMemo(
    () =>
      analytics
        ? buildChartData([
          "projects_created",
          "collections_created",
        ])
        : { chartData: [], lines: [] },
    [analytics]
  );

  const fileChart = useMemo(
    () =>
      analytics
        ? buildChartData([
          "project_files_uploaded",
          "collection_files_uploaded",
        ])
        : { chartData: [], lines: [] },
    [analytics]
  );

  const chatChart = useMemo(
    () =>
      analytics
        ? buildChartData([
          "project_chats_created",
          "collection_chats_created",
        ])
        : { chartData: [], lines: [] },
    [analytics]
  );

  /**
   * Loading / error states
   */
  if (loading) {
    return (
      <div className="rounded-2xl border border-[#ECEEF4] bg-white p-6">
        <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
          Loading analytics…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-[#ECEEF4] bg-white p-6">
        <div className="flex h-[300px] items-center justify-center text-sm text-red-500">
          Failed to load analytics
        </div>
      </div>
    );
  }

  /**
   * UI
   */
  return (
    <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
      <AnalyticsChartCard
        title="Projects and Collections Over Time"
        subtitle=""
        {...projectChart}
      />

      <AnalyticsChartCard
        title="Files Uploaded in Projects and Collections"
        subtitle=""
        {...fileChart}
      />

      {/* Spans the full width under the two cards above, as in the design */}
      <AnalyticsChartCard
        className="lg:col-span-2"
        title="Chats Created in Projects and with Collections"
        subtitle=""
        {...chatChart}
      />
    </div>
  );
}
